import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type {
  AiVerificationAssignmentSet,
  AiVerificationQuestionType,
  AiVerificationTally,
} from '@citizen-walk/shared-types';
import { httpsCallable } from 'firebase/functions';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { functions } from '../../lib/firebase';
import { PostDetailModal, type PostDetailModalPost } from './PostDetailModal';
import {
  fetchAllPostsWithSummary,
  fetchVerificationTemplateQuestions,
  useAdminAssignmentSets,
  useVerificationResults,
  type QuestionMeta,
  type VerificationResultPost,
} from './useVerificationResults';
import { useVerificationTemplates } from './useVerificationTemplates';
import {
  buildSummaryCsv,
  buildSummaryRowQuestions,
  collectSummaryColumns,
  getSubColumnValue,
} from './verificationSummaryCsv';

/**
 * Sprint BG-DataSettings 11 / GAP-F19 — 詳細 CSV download 結線。
 *
 * exportVerificationDetailCsv callable を呼出して CSV 文字列を取得 → BOM 付与 → Blob で
 * 「クライアント側ダウンロード」を実現する pure helper (test 容易性のため exported)。
 */
export function triggerCsvDownload(csv: string, filename: string): void {
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Sprint X + Admin Wave AW4 — Verification 結果閲覧ページ (admin、Phase 5)。
 *
 * 1. AssignmentSet selector — 地区の全 set (published / draft / closed) から 1 件選択
 * 2. 対象投稿一覧 — 選択中 set の candidatePostIds + 各 post の verificationSummary (accordion)
 * 3. 詳細 expand — perPost / perClassification の tally を type 別に集計表示
 * 4. 「📋 個票」ボタン (AW4 #14) — 投稿ごとの参加者×質問 回答表モーダル
 * 5. 横断 wide サマリ表 + 「📊 サマリ CSV」 (AW4 #7、client-side buildSummaryCsv)
 *    — set 未選択でも「地区全体の投稿サマリ」を出せる
 * 6. 「📥 詳細 CSV」 — exportVerificationDetailCsv callable (参加者×質問 flatten、server-side)
 *
 * 認可: master / district admin (担当地区) のみ閲覧。rules で deny + UI でも guard。
 */

function renderTallyByType(type: AiVerificationQuestionType, tally: AiVerificationTally): string {
  switch (type) {
    case 'three_state':
      return `妥当 ${tally.valid ?? 0} / 中立 ${tally.neutral ?? 0} / 不当 ${tally.invalid ?? 0}`;
    case 'binary':
      return `妥当 ${tally.valid ?? 0} / 不当 ${tally.invalid ?? 0}`;
    case 'yes_no':
      return `はい ${tally.yes ?? 0} / いいえ ${tally.no ?? 0}`;
    case 'five_scale': {
      const avg =
        tally.scaleCount && tally.scaleCount > 0 ? (tally.scaleSum ?? 0) / tally.scaleCount : null;
      return `平均 ${avg !== null ? avg.toFixed(2) : '—'} (N=${tally.scaleCount ?? 0})`;
    }
    case 'comment':
      return `回答 ${tally.textCount ?? 0} 件`;
    case 'custom_choice': {
      const entries = Object.entries(tally.choiceCounts ?? {});
      if (entries.length === 0) return '回答なし';
      return entries.map(([v, c]) => `${v}: ${c}`).join(' / ');
    }
    default:
      return '—';
  }
}

const STATUS_LABEL: Record<AiVerificationAssignmentSet['status'], string> = {
  draft: '下書き',
  published: '配信中',
  closed: '終了',
};

const STATUS_BADGE: Record<AiVerificationAssignmentSet['status'], string> = {
  draft: 'bg-slate-300 text-slate-800',
  published: 'bg-success-600 text-white',
  closed: 'bg-slate-500 text-white',
};

/** サブカラム種別ごとの色付け (妥当=緑、どちらでもない=灰、妥当ではない=赤)。 */
function tallyKeyClass(key: string): string {
  switch (key) {
    case 'valid':
    case 'yes':
      return 'text-success-700';
    case 'invalid':
    case 'no':
      return 'text-danger-700';
    default:
      return 'text-slate-700';
  }
}

export function VerificationResultsPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const { state: authState } = useAdminAuth();
  const districtState = useDistrict(districtId ?? null);
  const setsState = useAdminAssignmentSets(districtId ?? null);
  const templatesState = useVerificationTemplates(districtId ?? null);
  const { toast } = useToast();

  const [selectedSetId, setSelectedSetId] = useState<string>('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [questionMeta, setQuestionMeta] = useState<QuestionMeta[]>([]);
  const [csvDownloading, setCsvDownloading] = useState(false);
  // AW4 #14 — 個票モーダル
  const [detailPost, setDetailPost] = useState<PostDetailModalPost | null>(null);
  // AW4 #7 — 横断サマリ
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryPosts, setSummaryPosts] = useState<VerificationResultPost[] | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  /** GAP-F19 — 詳細 CSV (server-side、参加者×質問 flatten)。 */
  async function handleDetailCsvDownload() {
    if (!districtId) return;
    setCsvDownloading(true);
    try {
      const fn = httpsCallable<{ districtId: string }, { csv: string; rowCount: number }>(
        functions,
        'exportVerificationDetailCsv',
      );
      const res = await fn({ districtId });
      const today = new Date().toISOString().slice(0, 10);
      triggerCsvDownload(res.data.csv, `verification-detail_${districtId}_${today}.csv`);
      toast('success', `詳細 CSV ダウンロード完了 (${res.data.rowCount} 行)`);
    } catch (e) {
      toast('error', `詳細 CSV ダウンロード失敗: ${(e as Error).message}`);
    } finally {
      setCsvDownloading(false);
    }
  }

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canView = isMaster || isDistrictAdminOfThis;

  const sets = setsState.status === 'ready' ? setsState.items : [];
  const selectedSet = sets.find((s) => s.id === selectedSetId) ?? null;
  const resultsState = useVerificationResults(districtId ?? null, selectedSet);

  // selected set 変更時に template の question meta を fetch
  useEffect(() => {
    if (!districtId || !selectedSet) {
      setQuestionMeta([]);
      return;
    }
    let cancelled = false;
    fetchVerificationTemplateQuestions(districtId, selectedSet.templateId)
      .then((m) => {
        if (!cancelled) setQuestionMeta(m);
      })
      .catch(() => {
        if (!cancelled) setQuestionMeta([]);
      });
    return () => {
      cancelled = true;
    };
  }, [districtId, selectedSet?.id, selectedSet?.templateId]);

  function togglePost(postId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  }

  function getQuestionLabel(scope: 'perPost' | 'perClassification', qid: string): string {
    const q = questionMeta.find((m) => m.scope === scope && m.id === qid);
    return q?.label || qid;
  }

  function getQuestionType(
    scope: 'perPost' | 'perClassification',
    qid: string,
  ): AiVerificationQuestionType {
    const q = questionMeta.find((m) => m.scope === scope && m.id === qid);
    return (q?.type as AiVerificationQuestionType) ?? 'three_state';
  }

  // ============================================================
  // AW4 #7 — 横断サマリ (set 選択時はその post、未選択時は地区全体)
  // ============================================================

  /** 全テンプレを横断して questionId → { label, type } を解決 (横断サマリ/CSV 用)。 */
  const resolveQuestionMeta = useMemo(() => {
    const templates = templatesState.status === 'ready' ? templatesState.items : [];
    const map = new Map<string, { label: string; type: string }>();
    for (const t of templates) {
      for (const q of t.perPostQuestions) {
        map.set(`perPost|${q.id}`, { label: q.label, type: q.type });
      }
      for (const q of t.perClassificationQuestions) {
        map.set(`perClassification|${q.id}`, { label: q.label, type: q.type });
      }
    }
    return (scope: 'perPost' | 'perClassification', qid: string) =>
      map.get(`${scope}|${qid}`) ?? { label: qid, type: 'three_state' };
  }, [templatesState]);

  // 横断サマリの対象 posts: set 選択時はその結果、未選択時は地区全体 (lazy fetch)
  async function openSummary() {
    if (!districtId) return;
    setSummaryOpen(true);
    if (selectedSet && resultsState.status === 'ready') {
      setSummaryPosts(resultsState.posts.filter((p) => p.summary));
      return;
    }
    // set 未選択 → 地区全体を fetch
    setSummaryLoading(true);
    try {
      const posts = await fetchAllPostsWithSummary(districtId);
      setSummaryPosts(posts);
    } catch (e) {
      toast('error', `サマリ取得失敗: ${(e as Error).message}`);
      setSummaryPosts([]);
    } finally {
      setSummaryLoading(false);
    }
  }

  const summaryRows = useMemo(() => {
    const posts = summaryPosts ?? [];
    return posts
      .filter((p) => p.summary)
      .map((p) => {
        const summary = p.summary!;
        const questions = buildSummaryRowQuestions(summary, resolveQuestionMeta);
        return {
          post: {
            id: p.id,
            title: p.title,
            authorNickname: p.authorNickname,
            authorUid: p.authorUid,
          },
          responseCount: summary.completedResponses || summary.totalResponses || 0,
          // 自分の投稿への回答 / 他の人の投稿への回答 (完了数)。分離集計前の summary は null (CSV は空欄)。
          ownResponseCount: summary.own ? summary.own.completedResponses : null,
          othersResponseCount: summary.others ? summary.others.completedResponses : null,
          questions,
        };
      })
      .sort((a, b) => b.responseCount - a.responseCount);
  }, [summaryPosts, resolveQuestionMeta]);

  function handleSummaryCsv() {
    if (summaryRows.length === 0) {
      toast('error', 'サマリ対象がありません。');
      return;
    }
    const csv = buildSummaryCsv(summaryRows);
    const today = new Date().toISOString().slice(0, 10);
    triggerCsvDownload(csv, `verification-summary_${districtId}_${today}.csv`);
    toast('success', `サマリ CSV ダウンロード完了 (${summaryRows.length} 投稿)`);
  }

  const summaryColumns = useMemo(() => collectSummaryColumns(summaryRows), [summaryRows]);

  return (
    <>
      <header className="mb-6">
        <Link
          to={districtId ? `/districts/${districtId}` : '/districts'}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← 地区詳細
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">
          AI 検証結果
          {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          REQ-4.3.20〜25 — 配分セット別の検証集計 + 投稿ごとの個票 + 横断サマリ表 / CSV。
        </p>
      </header>

      {!canView && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">
            この地区の検証結果を閲覧する権限がありません (role: {authState.role ?? 'none'}、地区:{' '}
            {districtId})。
          </p>
        </Card>
      )}

      {/* AssignmentSet selector */}
      <Card padding="md" className="mb-6">
        <h2 className="text-base font-bold text-slate-900 mb-3">配分セット選択</h2>
        {setsState.status === 'loading' && <p className="text-sm text-slate-600">読込中…</p>}
        {setsState.status === 'error' && (
          <p className="text-sm text-danger-700">読込エラー: {setsState.error.message}</p>
        )}
        {setsState.status === 'ready' && sets.length === 0 && (
          <p className="text-sm text-slate-600">配分セットがまだありません。</p>
        )}
        {setsState.status === 'ready' && sets.length > 0 && (
          <div className="flex items-center gap-3 flex-wrap">
            <label htmlFor="set-selector" className="text-sm text-slate-700 shrink-0">
              対象セット:
            </label>
            <select
              id="set-selector"
              aria-label="配分セット"
              className="flex-1 min-w-[200px] rounded border border-slate-300 px-3 py-1.5 text-sm"
              value={selectedSetId}
              onChange={(e) => {
                setSelectedSetId(e.target.value);
                setExpanded(new Set());
              }}
            >
              <option value="">— 選択してください —</option>
              {sets.map((s) => (
                <option key={s.id} value={s.id}>
                  [{STATUS_LABEL[s.status]}] {s.displayName || '(無題)'} (
                  {s.candidatePostIds.length} 件)
                </option>
              ))}
            </select>
            {selectedSet && (
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${STATUS_BADGE[selectedSet.status]}`}
              >
                {STATUS_LABEL[selectedSet.status]}
              </span>
            )}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void openSummary()}
              title="投稿×質問の横断サマリ表を表示 (set 未選択時は地区全体)"
            >
              📊 横断サマリ
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void handleDetailCsvDownload()}
              disabled={csvDownloading}
              title="参加者×質問の個別回答を long format CSV (BOM 付 Excel 互換) でダウンロード"
            >
              {csvDownloading ? '⏳ 詳細 CSV 生成中…' : '📥 詳細 CSV'}
            </Button>
          </div>
        )}
      </Card>

      {/* AW4 #7 — 横断 wide サマリ表 */}
      {summaryOpen && (
        <Card padding="md" className="mb-6">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <h2 className="text-base font-bold text-slate-900">
              横断サマリ表{' '}
              <span className="text-xs font-normal text-slate-500">
                ({selectedSet ? `セット「${selectedSet.displayName || '(無題)'}」` : '地区全体'} ·{' '}
                {summaryRows.length} 投稿)
              </span>
            </h2>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" onClick={handleSummaryCsv}>
                📊 サマリ CSV
              </Button>
              <button
                type="button"
                onClick={() => setSummaryOpen(false)}
                className="text-xs text-slate-500 hover:underline"
              >
                閉じる
              </button>
            </div>
          </div>
          {summaryLoading ? (
            <p className="text-sm text-slate-600">サマリ集計中…</p>
          ) : summaryRows.length === 0 ? (
            <p className="text-sm text-slate-600">回答が集まった投稿がありません。</p>
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded">
              <table className="text-xs border-collapse min-w-full">
                <thead>
                  <tr className="bg-slate-100">
                    <th
                      rowSpan={2}
                      className="text-left p-2 border-r border-b border-slate-200 sticky left-0 bg-slate-100 z-10 min-w-[14rem]"
                    >
                      投稿
                    </th>
                    <th
                      rowSpan={2}
                      className="text-left p-2 border-r border-b border-slate-200 whitespace-nowrap"
                    >
                      回答数
                    </th>
                    {summaryColumns.map((col) => (
                      <th
                        key={`${col.scope}-${col.rank ?? ''}-${col.questionId}`}
                        colSpan={col.subColumns.length}
                        className={`text-left p-2 border-r border-b border-slate-200 ${col.scope === 'perClassification' ? 'bg-amber-50' : ''}`}
                        title={`${col.questionType} / ${col.questionId}`}
                      >
                        <div className="text-[10px] text-slate-500 font-normal">{col.prefix}</div>
                        <div className="font-semibold whitespace-nowrap">
                          {col.questionLabel || col.questionId}
                        </div>
                        <div className="text-[10px] text-slate-400 font-normal">
                          {col.questionType}
                        </div>
                      </th>
                    ))}
                  </tr>
                  <tr className="bg-slate-50">
                    {summaryColumns.flatMap((col) =>
                      col.subColumns.map((sub) => (
                        <th
                          key={`sub-${col.scope}-${col.rank ?? ''}-${col.questionId}-${sub.key}`}
                          className={`text-left p-1.5 border-r border-b border-slate-200 whitespace-nowrap font-normal text-[10px] ${tallyKeyClass(sub.key)}`}
                        >
                          {sub.label}
                        </th>
                      )),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {summaryRows.map((r) => {
                    const tallyMap = new Map<string, AiVerificationTally>();
                    for (const q of r.questions) {
                      tallyMap.set(`${q.scope}|${q.rank ?? ''}|${q.questionId}`, q.tally);
                    }
                    return (
                      <tr key={r.post.id} className="hover:bg-slate-50">
                        <td
                          className="p-2 border-r border-b border-slate-200 sticky left-0 bg-white cursor-pointer hover:bg-primary-50 min-w-[14rem]"
                          onClick={() =>
                            setDetailPost({
                              id: r.post.id,
                              title: r.post.title,
                              authorNickname: r.post.authorNickname,
                              authorUid: r.post.authorUid,
                            })
                          }
                        >
                          <div className="font-semibold text-primary-700 hover:underline truncate max-w-[14rem]">
                            {r.post.title || '(無題)'}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {r.post.authorNickname}{' '}
                            <span className="font-mono">…{r.post.authorUid.slice(-6)}</span>
                          </div>
                        </td>
                        <td className="p-2 border-r border-b border-slate-200 text-right">
                          <strong>{r.responseCount}</strong>
                        </td>
                        {summaryColumns.flatMap((col) => {
                          const tally = tallyMap.get(
                            `${col.scope}|${col.rank ?? ''}|${col.questionId}`,
                          );
                          return col.subColumns.map((sub) => (
                            <td
                              key={`${r.post.id}-${col.scope}-${col.rank ?? ''}-${col.questionId}-${sub.key}`}
                              className={`p-2 border-r border-b border-slate-200 text-right ${col.scope === 'perClassification' ? 'bg-amber-50/30' : ''} ${tallyKeyClass(sub.key)}`}
                            >
                              {tally ? (
                                getSubColumnValue(tally, sub.key) || (
                                  <span className="text-slate-300">—</span>
                                )
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                          ));
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[11px] text-slate-500 mt-2">
            ※ 投稿タイトルをクリックすると、その投稿の参加者ごとの個別回答（個票）を表示します。
            「サマリ CSV」は BOM 付き Excel 互換でダウンロードします。
          </p>
        </Card>
      )}

      {/* 結果一覧 (accordion) */}
      {selectedSet && (
        <section>
          <h2 className="text-base font-bold text-slate-900 mb-3">対象投稿一覧</h2>

          {resultsState.status === 'idle' && (
            <Card padding="md">
              <p className="text-sm text-slate-600">セットを選択してください。</p>
            </Card>
          )}
          {resultsState.status === 'loading' && (
            <Card padding="md">
              <p className="text-sm text-slate-600">読込中…</p>
            </Card>
          )}
          {resultsState.status === 'error' && (
            <Card padding="md" className="border-danger-600">
              <p className="text-sm text-danger-700">読込エラー: {resultsState.error.message}</p>
            </Card>
          )}
          {resultsState.status === 'ready' && resultsState.posts.length === 0 && (
            <Card padding="md">
              <p className="text-sm text-slate-600">対象投稿がありません。</p>
            </Card>
          )}
          {resultsState.status === 'ready' && resultsState.posts.length > 0 && (
            <ul className="grid grid-cols-1 gap-2">
              {resultsState.posts.map((p) => {
                const isExpanded = expanded.has(p.id);
                const summary = p.summary;
                const total = summary?.totalResponses ?? 0;
                const completed = summary?.completedResponses ?? 0;
                const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
                return (
                  <li key={p.id}>
                    <Card padding="md">
                      <div className="flex items-start justify-between gap-3">
                        <button
                          type="button"
                          className="min-w-0 flex-1 text-left"
                          onClick={() => togglePost(p.id)}
                          aria-expanded={isExpanded}
                        >
                          <p className="text-sm font-bold text-slate-900 truncate">{p.title}</p>
                          <p className="mt-0.5 text-[11px] text-slate-500">
                            {p.authorNickname && `${p.authorNickname} / `}
                            <code>{p.id}</code>
                          </p>
                          <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                              回答 {total} 件
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-success-100 text-success-700">
                              完了 {completed} 件 ({completionRate}%)
                            </span>
                            {/* 自分の投稿への回答 / 他の人の投稿への回答 (完了数)。分離集計が付いた summary のみ。 */}
                            {summary?.own && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-50 text-primary-700">
                                自分の投稿への回答 {summary.own.completedResponses} 件
                              </span>
                            )}
                            {summary?.others && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                                他の人の投稿への回答 {summary.others.completedResponses} 件
                              </span>
                            )}
                            {summary?.lastAggregatedAt && (
                              <span className="text-[10px] text-slate-400">
                                集計: {new Date(summary.lastAggregatedAt).toLocaleString('ja-JP')}
                              </span>
                            )}
                          </div>
                        </button>
                        <div className="flex items-center gap-2 shrink-0 mt-1">
                          {/* AW4 #14 — 個票モーダルを開く */}
                          <button
                            type="button"
                            onClick={() =>
                              setDetailPost({
                                id: p.id,
                                title: p.title,
                                authorNickname: p.authorNickname,
                                authorUid: p.authorUid,
                              })
                            }
                            className="text-xs text-primary-700 hover:underline"
                            title="参加者ごとの個別回答を表示"
                          >
                            📋 個票
                          </button>
                          <button
                            type="button"
                            onClick={() => togglePost(p.id)}
                            aria-label="展開"
                            className="text-xs text-slate-400"
                          >
                            {isExpanded ? '▼' : '▶'}
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          {/* completion bar */}
                          <div className="mb-3">
                            <div className="h-2 w-full rounded bg-slate-200 overflow-hidden">
                              <div
                                className="h-full bg-success-600"
                                style={{ width: `${completionRate}%` }}
                                role="progressbar"
                                aria-valuenow={completionRate}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              />
                            </div>
                            <p className="mt-1 text-[11px] text-slate-500">
                              完了率: {completionRate}% ({completed} / {total})
                            </p>
                          </div>

                          {/* perPost */}
                          {summary && Object.keys(summary.perPost).length > 0 && (
                            <div className="mb-3">
                              <h3 className="text-xs font-bold text-slate-700 mb-1">
                                投稿全体への質問
                              </h3>
                              <ul className="text-xs grid grid-cols-1 gap-1">
                                {Object.entries(summary.perPost).map(([qid, tally]) => (
                                  <li key={qid} className="flex items-start gap-2">
                                    <span className="text-slate-500 shrink-0">
                                      {getQuestionLabel('perPost', qid)}:
                                    </span>
                                    <span className="text-slate-800">
                                      {renderTallyByType(getQuestionType('perPost', qid), tally)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* perClassification */}
                          {summary && Object.keys(summary.perClassification).length > 0 && (
                            <div>
                              <h3 className="text-xs font-bold text-slate-700 mb-1">
                                分類行への質問
                              </h3>
                              {Object.entries(summary.perClassification).map(([rank, qmap]) => (
                                <div key={rank} className="mb-2">
                                  <p className="text-[11px] font-semibold text-slate-600">
                                    rank {rank}
                                  </p>
                                  <ul className="text-xs grid grid-cols-1 gap-1 ml-3">
                                    {Object.entries(qmap).map(([qid, tally]) => (
                                      <li key={qid} className="flex items-start gap-2">
                                        <span className="text-slate-500 shrink-0">
                                          {getQuestionLabel('perClassification', qid)}:
                                        </span>
                                        <span className="text-slate-800">
                                          {renderTallyByType(
                                            getQuestionType('perClassification', qid),
                                            tally,
                                          )}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          )}

                          {(!summary ||
                            (Object.keys(summary.perPost).length === 0 &&
                              Object.keys(summary.perClassification).length === 0)) && (
                            <p className="text-xs text-slate-500">
                              この投稿への検証回答はまだありません。
                            </p>
                          )}
                        </div>
                      )}
                    </Card>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {/* AW4 #14 — 個票モーダル */}
      {detailPost && districtId && (
        <PostDetailModal
          districtId={districtId}
          post={detailPost}
          onClose={() => setDetailPost(null)}
        />
      )}
    </>
  );
}

// pure helper を test から import 可能に re-export
export { renderTallyByType };
